import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import './DevelopmentTab.css';

interface DevRecord {
  season_year: number;
  old_age: number;
  new_age: number;
  deltas: Record<string, number>;
}

interface Props {
  playerId: string;
  isPitcher: boolean;
}

const HITTER_ATTRS = ['power', 'contact', 'speed', 'fielding', 'arm', 'discipline'] as const;
const PITCHER_ATTRS = ['velocity', 'control', 'movement', 'deception'] as const;
const ATTR_LABELS: Record<string, string> = {
  power: 'POW', contact: 'CON', speed: 'SPD',
  fielding: 'FLD', arm: 'ARM', discipline: 'DIS',
  velocity: 'VEL', control: 'CTL', movement: 'MOV', deception: 'DEC',
};

function getColor(val: number): string {
  if (val >= 65) return '#00d4aa';
  if (val >= 50) return '#3b82f6';
  if (val >= 35) return '#f59e0b';
  return '#ef4444';
}

function getGrade(val: number): string {
  if (val >= 70) return 'A+';
  if (val >= 65) return 'A';
  if (val >= 60) return 'A-';
  if (val >= 57) return 'B+';
  if (val >= 53) return 'B';
  if (val >= 50) return 'B-';
  if (val >= 47) return 'C+';
  if (val >= 43) return 'C';
  if (val >= 40) return 'C-';
  if (val >= 35) return 'D+';
  if (val >= 30) return 'D';
  if (val >= 25) return 'D-';
  return 'F';
}

const DevelopmentTab: React.FC<Props> = ({ playerId, isPitcher }) => {
  const [records, setRecords] = useState<DevRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    loadHistory();
  }, [playerId]);

  async function loadHistory() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }

    const { data } = await supabase
      .from('season_advancement')
      .select('*')
      .eq('player_id', playerId)
      .order('season_year', { ascending: true });

    setRecords(data || []);
    setLoading(false);
  }

  if (loading) return <div className="dev-loading">Loading development history...</div>;

  const attrs = (isPitcher ? PITCHER_ATTRS : HITTER_ATTRS) as readonly string[];
  const hasData = records.some(r => {
    const d = r.deltas;
    return d && attrs.some(a => d[a] != null);
  });

  if (!hasData) {
    return (
      <div className="dev-tab">
        <p className="dev-empty">No development snapshots yet. Attribute history will appear after each season flip.</p>
      </div>
    );
  }

  return (
    <div className="dev-tab">
      <p className="dev-subtitle">Year-by-year attribute history. Bold numbers show the current value entering each season (after growth/decline applied).</p>
      <div className="dev-scroll">
        <table className="dev-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Age</th>
              {attrs.map(a => <th key={a}>{ATTR_LABELS[a] || a}</th>)}
              <th>Phase</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => {
              const d = r.deltas || {};
              return (
                <tr key={i}>
                  <td className="dev-year">{r.season_year}</td>
                  <td className="dev-age">{r.new_age ?? r.old_age + 1}</td>
                  {attrs.map(a => {
                    const val = d[a];
                    if (val == null) return <td key={a} className="dev-empty-cell">—</td>;
                    return (
                      <td key={a} className="dev-attr-cell">
                        <span className="dev-grade" style={{ color: getColor(val) }}>{getGrade(val)}</span>
                        <span className="dev-value" style={{ color: getColor(val) }}>{val}</span>
                      </td>
                    );
                  })}
                  <td className="dev-phase">{d.phase || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DevelopmentTab;
